// Helpers de moeda (máscara R$) + recálculo bidirecional de markup.
// Extraídos do catalog-form-page para reuso (ex.: tela de precificação de variações).
// Mantêm EXATAMENTE o mesmo comportamento do formulário de catálogo.
import { mask, unMask } from './util'

/** Formata dígitos/valor como moeda "R$ 1.234,56". Com allowEmpty, zero vira "". */
export const currencyFormat = (
    value: string | number | null | undefined,
    allowEmpty = false
): string => {
    if (value === null || value === undefined) value = '0'
    const digits = `${value}`.replace(/[^0-9]+/g, '')
    if (
        allowEmpty &&
        (!digits || digits === '0' || digits === '00' || digits === '000')
    )
        return ''
    return mask(digits, '', 'currency', {
        prefix: 'R$ ',
        decimalSeparator: ',',
        groupSeparator: '.',
        precision: 2,
    })
}

/** Converte a string mascarada "R$ 12,34" em centavos (1234). */
export const parseCurrencyToCents = (value: string): number => {
    if (!value.trim()) return 0
    const unmaskedValue = unMask(value, 'currency')
    return parseInt(unmaskedValue)
}

/** Centavos → string mascarada ("R$ 12,34"); 0/null → "". */
export const formatCentsToDisplay = (cents: number | null | undefined): string => {
    if (cents === null || cents === undefined || cents === 0) return ''
    return currencyFormat(cents.toString())
}

/** Venda = custo + markup%. Retorna string mascarada (ou "" se inválido). */
export const recalcSellingPriceFromMarkup = (
    priceCents: number,
    markup: number
): string => {
    if (priceCents <= 0 || isNaN(markup)) return ''
    const sellingCents = Math.round(priceCents + (priceCents * markup) / 100)
    return formatCentsToDisplay(sellingCents)
}

/** Markup% a partir de custo e venda. Retorna string com 2 casas (ou ""). */
export const recalcMarkupFromPrices = (
    priceCents: number,
    sellingCents: number
): string => {
    if (priceCents <= 0) return ''
    const markup = ((sellingCents - priceCents) / priceCents) * 100
    return markup.toFixed(2)
}

/**
 * Linha de preço com custo/venda/markup (strings). Reaplica a máscara e recalcula
 * o campo dependente ao editar um dos três — igual ao form de catálogo:
 * - editou custo: se há markup, recalcula venda; senão, recalcula markup pela venda.
 * - editou venda: recalcula markup (custo↔venda).
 * - editou markup: recalcula venda (custo+markup).
 */
export interface PriceTriple {
    price: string
    sellingPrice: string
    markupPercentage: string
}

export function recalcPriceTriple<T extends PriceTriple>(
    item: T,
    field: 'price' | 'sellingPrice' | 'markupPercentage',
    value: string
): T {
    const next = { ...item }
    if (field === 'price') {
        next.price = currencyFormat(value, true)
        const priceCents = parseCurrencyToCents(next.price)
        if (next.markupPercentage) {
            const markup = parseFloat(next.markupPercentage)
            if (!isNaN(markup)) {
                next.sellingPrice = recalcSellingPriceFromMarkup(priceCents, markup)
            }
        } else if (next.sellingPrice) {
            const sellingCents = parseCurrencyToCents(next.sellingPrice)
            if (sellingCents > 0) {
                next.markupPercentage = recalcMarkupFromPrices(priceCents, sellingCents)
            }
        }
    } else if (field === 'sellingPrice') {
        next.sellingPrice = currencyFormat(value, true)
        const sellingCents = parseCurrencyToCents(next.sellingPrice)
        const priceCents = parseCurrencyToCents(next.price)
        next.markupPercentage = recalcMarkupFromPrices(priceCents, sellingCents)
    } else {
        next.markupPercentage = value
        const markup = parseFloat(value)
        const priceCents = parseCurrencyToCents(next.price)
        next.sellingPrice = !isNaN(markup)
            ? recalcSellingPriceFromMarkup(priceCents, markup)
            : ''
    }
    return next
}
